import orderBy from 'lodash/orderBy';
import { Action } from 'typescript-fsa';

import { createReducer } from '@topconagriculture/frontend-utils';

import {
  addBunkReaderGroupNote,
  addBunkScores,
  addGroup,
  deleteBunkReaderGroupNote,
  deleteFeeding,
  deleteFeedingGroup,
  deleteGroup,
  editBunkReaderGroupNote,
  editGroup,
  editGroups,
  getAllGroups,
  getBunkReaderGroupById,
  getGroupById,
  getGroupList,
  updateGroup,
} from './group.actions';
import { Group, GroupNote } from './group.types';
import { BunkScoresResponse } from '../settings/settings.types';

export type GroupState = Group[];

export const initialState: GroupState = [];

const groupReducer = createReducer(initialState, {
  [getAllGroups.done.type]: (state: GroupState, action: Action<{ result: Group[] }>) =>
    action.payload.result.sort((elem1: Group, elem2: Group) => {
      return elem1.description.toLowerCase().localeCompare(elem2.description.toLowerCase());
    }),
  [getGroupList.done.type]: (state: GroupState, action: Action<{ result: Group[] }>) =>
    action.payload.result.sort((elem1: Group, elem2: Group) =>
      elem1.description.toLowerCase().localeCompare(elem2.description.toLowerCase()),
    ),
  [getGroupById.done.type]: (state: GroupState, action: Action<{ result: Group }>) => {
    const currentGroup = action.payload.result;
    return state.map((group) => {
      if (group.id === currentGroup.id) {
        return {
          ...currentGroup,
          feedings: currentGroup.feedings || [],
        };
      }
      return group;
    });
  },
  [getBunkReaderGroupById.done.type]: (state: GroupState, action: Action<{ params: string; result: Group }>) => {
    const { params: id, result } = action.payload;

    return state.map((group) => {
      if (group.id === id) {
        return {
          ...group,
          ...result,
          scores: result.scores,
          deliveries: result.deliveries,
        };
      }
      return group;
    });
  },
  [addBunkScores.done.type]: (state: GroupState, action: Action<{ params: string; result: BunkScoresResponse }>) => {
    const { result } = action.payload;
    return state.map((group) => {
      if (group.id === result.groupId) {
        return {
          ...group,
          lastBunkScoreResult: { ...group.lastBunkScoreResult, ...result },
        };
      }
      return group;
    });
  },
  [addBunkReaderGroupNote.done.type]: (
    state: GroupState,
    action: Action<{ result: { groupId: string; note: GroupNote } }>,
  ) => {
    const { result } = action.payload;
    return state.map((group) => {
      if (group.id === result.groupId) {
        return {
          ...group,
          notes: [...(group.notes || []), result.note],
        };
      }
      return group;
    });
  },
  [editBunkReaderGroupNote.done.type]: (
    state: GroupState,
    action: Action<{ result: { groupId: string; bunkReaderNoteId: string; note: GroupNote } }>,
  ) => {
    const { result } = action.payload;

    return state.map((group) => {
      if (group.id === result.groupId) {
        return {
          ...group,
          notes: group.notes?.map((note) => {
            if (note.id === result.note.id) {
              return result.note;
            }
            return note;
          }),
        };
      }
      return group;
    });
  },
  [deleteBunkReaderGroupNote.done.type]: (
    state: GroupState,
    action: Action<{ params: { groupId: string; noteId } }>,
  ) => {
    const { params } = action.payload;

    return state.map((group) => {
      if (group.id === params.groupId) {
        return {
          ...group,
          notes: group.notes!.filter((note) => note.id !== params.noteId),
        };
      }
      return group;
    });
  },
  [addGroup.done.type]: (state: GroupState, action: Action<{ result: Group }>) => {
    const group = action.payload.result;
    return [...state, group];
  },
  [deleteFeedingGroup.done.type]: (
    state: GroupState,
    action: Action<{ params: { feedingId: string; groupId: string } }>,
  ) => {
    const { feedingId, groupId } = action.payload.params;
    return state.map((group) => {
      if (group.id === groupId) {
        return {
          ...group,
          feedings: orderBy(
            (group.feedings || []).filter((feeding) => feeding.id !== feedingId),
            'feedingPeriodId',
            'asc',
          ),
        };
      }
      return group;
    });
  },
  [updateGroup.done.type]: (state: GroupState, action: Action<{ result: Group }>) => {
    const updatedGroup = action.payload.result;
    return state.map((group) => {
      if (group.id === updatedGroup.id) {
        return {
          ...group,
          ...updatedGroup,
        };
      }
      return group;
    });
  },
  [editGroup.done.type]: (state: GroupState, action: Action<{ result: Group }>) => {
    const updatedGroup = action.payload.result;
    return state.map((group) => {
      if (group.id === updatedGroup.id) {
        return {
          ...group,
          ...updatedGroup,
          feedings: updatedGroup.feedings || [],
        };
      }
      return group;
    });
  },
  [editGroups.done.type]: (state: GroupState, action: Action<{ result: Group[] }>) => {
    const updatedGroups = action.payload.result;
    const updatedGroupIds = updatedGroups.map(({ id }) => id);

    return state.map((group) => {
      if (updatedGroupIds.includes(group.id)) {
        const updatedGroup = updatedGroups.find(({ id }) => id === group.id)!;
        return {
          ...group,
          ...updatedGroup,
          feedings: updatedGroup.feedings || [],
        };
      }
      return group;
    });
  },
  [deleteGroup.done.type]: (state: GroupState, action: ReturnType<typeof deleteGroup.done>) => {
    const groupId = String(action.payload.result);
    return [...state.filter((group) => group.id !== groupId)];
  },
  [deleteFeeding.done.type]: (state: GroupState, action: ReturnType<typeof deleteFeeding.done>) => {
    return state.map((group) => {
      return group.id === action.payload.params.groupId
        ? {
            ...group,
            feedings: group.feedings && group.feedings.filter((x) => x.id !== action.payload.params.feedingId),
          }
        : group;
    });
  },
});

export default groupReducer;
